import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  RIGHTS_CATALOG,
  RIGHTS_KEYS,
  roleGrants,
  savedRoleRights,
  withDefaultOnRights,
  type RightsMap,
  type SettingsRole,
} from '@ehr/shared';
import { api } from '../lib/api.js';
import { RIGHTS_CHANGED_EVENT } from '../lib/myRights.js';
import { IconSearch, IconX, msg } from './usersShared.js';
import './usersModal.css';

/**
 * Access Rights (per user) and Role Rights (per role) — EHR_08-14-V38 renderUserRightsOverlay /
 * renderRoleRightsPopup (ehr#60). Toggles over the V38 feature catalog. A user rides their
 * role ('role' mode) until a toggle overrides it ('custom'); "Apply role permissions" clears
 * the overrides. Admins always have everything and their toggles are locked.
 */

export interface UserRightsState {
  mode: 'role' | 'custom';
  overrides: RightsMap;
}

function Switch({
  on,
  locked,
  label,
  onChange,
}: {
  on: boolean;
  locked?: boolean;
  label: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={`um-sw${locked ? ' locked' : ''}`}>
      <input
        type="checkbox"
        checked={on}
        disabled={locked}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span />
    </label>
  );
}

function Overlay({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="um-fmap-ov" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      {children}
    </div>
  );
}

function RolePill({ name }: { name: string }) {
  return (
    <span className="um-role-pill">
      <span>Role</span>
      {name}
    </span>
  );
}

const matches = (q: string, group: string, label: string, key: string) =>
  !q || `${group} ${label} ${key}`.toLowerCase().includes(q);

/** Per-user Access Rights window. Each toggle saves right away (as in the mockup). */
export function UserRightsWindow({
  userId,
  userName,
  roleName,
  baseRole,
  roleRights,
  initial,
  wizard,
  onClose,
  onSaved,
  footer,
}: {
  userId: string;
  userName: string;
  roleName: string;
  baseRole: SettingsRole;
  /** The role's own map (defaults filled in). */
  roleRights: RightsMap;
  initial: UserRightsState | null;
  /**
   * Add User wizard step 3. ehr#204: the switches work here too (they were read-only when the
   * profile step said "Role rights"); while the user still follows their role, a note says that
   * changing a switch sets that right for this user only.
   */
  wizard?: boolean;
  onClose: () => void;
  onSaved: (r: UserRightsState) => void;
  footer?: ReactNode;
}) {
  const [state, setState] = useState<UserRightsState>(initial ?? { mode: 'role', overrides: {} });
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const isAdmin = baseRole === 'admin';
  const ql = q.trim().toLowerCase();
  const hasOverrides = state.mode === 'custom' && Object.keys(state.overrides).length > 0;

  const put = (next: UserRightsState) => {
    const prev = state;
    setState(next);
    setErr(null);
    api
      .put(`/settings/users/${userId}/rights`, next)
      .then(() => {
        window.dispatchEvent(new Event(RIGHTS_CHANGED_EVENT));
        onSaved(next);
      })
      .catch((e: unknown) => {
        setState(prev);
        setErr(`Not saved. ${msg(e)}`);
      });
  };
  // ehr#703: the rights that start on (Practice-wide lists) count as granted unless saved off
  const fromRole = (k: string) => isAdmin || roleGrants(roleRights, k);
  const on = (k: string) =>
    isAdmin
      ? true
      : state.mode === 'custom' && typeof state.overrides[k] === 'boolean'
        ? state.overrides[k]!
        : fromRole(k);
  const toggle = (k: string, v: boolean) => {
    const overrides = { ...(state.mode === 'custom' ? state.overrides : {}) };
    if (v === fromRole(k)) delete overrides[k];
    else overrides[k] = v;
    put({ mode: 'custom', overrides });
  };

  const groups = RIGHTS_CATALOG.map((g) => ({
    ...g,
    perms: g.perms.filter((p) => matches(ql, g.group, p.label, p.key)),
  })).filter((g) => g.perms.length > 0);

  return (
    <Overlay onClose={onClose}>
      <div className="urights-dialog" role="dialog" aria-modal="true" aria-label="User rights">
        <div className="um-fmap-head">
          <div>
            <h3>{userName} — Rights</h3>
            <div className="um-role-pill-row">
              <RolePill name={roleName} />
            </div>
            <p>
              Feature access for this user. Each toggle overrides the user&apos;s role ({roleName});{' '}
              <strong>Apply role permissions</strong> clears overrides so they inherit the role,
              then tweak individual features.
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            {IconX}
          </button>
        </div>
        <div className="urights-toolbar">
          <label className="urights-search">
            {IconSearch}
            <input
              type="text"
              placeholder="Search features…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoComplete="off"
            />
          </label>
          <button
            className="btn"
            disabled={isAdmin || !hasOverrides}
            title={
              isAdmin
                ? 'Admin role already has full access'
                : hasOverrides
                  ? 'Reset this user to their role default permissions'
                  : 'This user already inherits the role permissions'
            }
            onClick={() => put({ mode: 'role', overrides: {} })}
          >
            Apply role permissions
          </button>
        </div>
        <div className="urights-body">
          {err && <div className="config-err">{err}</div>}
          {isAdmin && (
            <div className="urights-adminnote">
              This user&apos;s role is an <strong>Administrator</strong> — full access to every
              feature, and it can&apos;t be restricted here.
            </div>
          )}
          {wizard && !isAdmin && state.mode === 'role' && (
            <div className="um-amber-note">
              This user inherits their role&apos;s rights. Turn a switch on or off to set that right
              for this user only; the rest keep following the role.
            </div>
          )}
          {groups.map((g) => (
            <div className="urights-group" key={g.group}>
              <div className="urights-group-label">{g.group}</div>
              <div className="urights-rows-grid">
                {g.perms.map((p) => {
                  const overridden =
                    !isAdmin &&
                    state.mode === 'custom' &&
                    typeof state.overrides[p.key] === 'boolean';
                  return (
                    <div className="urights-row" key={p.key}>
                      <div className="urights-row-label">
                        {p.label}
                        {overridden && (
                          <span className="urights-ovr" title="Overrides the role default">
                            override
                          </span>
                        )}
                      </div>
                      <Switch
                        on={on(p.key)}
                        locked={isAdmin}
                        label={p.label}
                        onChange={(v) => toggle(p.key, v)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <div className="urights-noresults">No features match your search.</div>
          )}
        </div>
        <div className="um-fmap-foot">
          {footer ?? (
            <button className="btn primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}

/** Role Rights window (User Roles → Edit / Add Role Rights). Saves with Save. */
export function RoleRightsWindow({
  roleKey,
  roleName,
  rights,
  onClose,
  onSaved,
}: {
  roleKey: string;
  roleName: string;
  rights: RightsMap;
  onClose: () => void;
  onSaved: (rights: RightsMap) => void;
}) {
  const isAdmin = roleKey === 'sys:admin';
  // ehr#703: the rights that start on are shown on unless the role saved them off
  const filled = useMemo(() => withDefaultOnRights(rights), [rights]);
  const [draft, setDraft] = useState<RightsMap>(() => ({ ...filled }));
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setDraft({ ...filled }), [filled]);
  const count = useMemo(
    () => (isAdmin ? RIGHTS_KEYS.length : RIGHTS_KEYS.filter((k) => draft[k]).length),
    [draft, isAdmin],
  );
  const ql = q.trim().toLowerCase();
  const dirty = JSON.stringify(draft) !== JSON.stringify(filled);
  const setAll = (v: boolean) =>
    setDraft(Object.fromEntries(RIGHTS_KEYS.map((k) => [k, v])) as RightsMap);
  const save = () => {
    setSaving(true);
    setErr(null);
    // ehr#703: a right that starts on keeps its false (left out, it would read as on again)
    const clean = savedRoleRights(draft);
    api
      .put(`/settings/team/role-rights/${encodeURIComponent(roleKey)}`, { rights: clean })
      .then(() => {
        window.dispatchEvent(new Event(RIGHTS_CHANGED_EVENT));
        onSaved(clean);
        onClose();
      })
      .catch((e: unknown) => setErr(msg(e)))
      .finally(() => setSaving(false));
  };
  const groups = RIGHTS_CATALOG.map((g) => ({
    ...g,
    perms: g.perms.filter((p) => matches(ql, g.group, p.label, p.key)),
  })).filter((g) => g.perms.length > 0);

  return (
    <Overlay onClose={onClose}>
      <div
        className="urights-dialog rr-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Role rights"
      >
        <div className="um-fmap-head">
          <div>
            <h3>{roleName} — Role Rights</h3>
            <div className="um-role-pill-row">
              <RolePill name={roleName} />
            </div>
            <p>
              Which features everyone on this role can use. A user&apos;s own Access Rights can
              still override single features.
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            {IconX}
          </button>
        </div>
        {!isAdmin && (
          <div className="rr-toolbar">
            <label className="urights-search">
              {IconSearch}
              <input
                type="text"
                placeholder="Search features…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            <div className="rr-count-row">
              <div>
                <strong>{count}</strong> of {RIGHTS_KEYS.length} features enabled
              </div>
              <div className="rr-count-btns">
                <button className="btn" onClick={() => setAll(true)}>
                  Select All
                </button>
                <button className="btn" onClick={() => setAll(false)}>
                  Deselect All
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="urights-body">
          {err && <div className="config-err">{err}</div>}
          {isAdmin ? (
            <div className="urights-adminnote">
              The Admin role always has every permission and can&apos;t be restricted — this
              guarantees an org can never lock itself out.
            </div>
          ) : (
            <>
              <div className="rr-perm-grid">
                {groups.map((g) => (
                  <div className="rr-perm-group" key={g.group}>
                    <div className="urights-group-label">{g.group}</div>
                    {g.perms.map((p) => (
                      <label className="rr-perm-check" key={p.key}>
                        <span>{p.label}</span>
                        <Switch
                          on={!!draft[p.key]}
                          label={p.label}
                          onChange={(v) => setDraft({ ...draft, [p.key]: v })}
                        />
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {groups.length === 0 && (
                <div className="urights-noresults">No features match your search.</div>
              )}
            </>
          )}
        </div>
        <div className="um-fmap-foot">
          <button className="btn" onClick={onClose}>
            {dirty ? 'Cancel' : 'Close'}
          </button>
          {!isAdmin && (
            <button className="btn primary" disabled={!dirty || saving} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}
